import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import EditNoteIcon from '@mui/icons-material/EditNote';

import {
  IDeedTemplate,
  useCancelDeedRun,
  useDeedRunWordDocument,
  useDeedRunsOfLegalTransaction,
  useDeedTemplates,
  useStartDeedRun,
} from '../hooks/reactQuerys/DeedGenerationQueries';
import {
  base64ToBlob,
  triggerBrowserDownload,
} from './DocumentDirectory/documentDownload';
import {
  NO_TEMPLATE_TEXT,
  NO_TRANSACTION_TYPE_TEXT,
  initialVariant,
  needsVariantChoice,
  offerMode,
  offeredTemplates,
  preselectedTemplate,
  runProvenance,
  runStatusText,
  templateLabel,
} from './deedGeneration';
import { useAlert } from '../context/AlertContext';
import { IDocument } from '../Interfaces/IDocument';
import { DinoDeedDialog } from './DinoDeedDialog';
import { apiErrorText } from '../Admin/ArnoTop/apiErrorText';

/**
 * „Urkunde erstellen" im Vorgang (#1245).
 *
 * Der Weg für den Mitarbeiter, ohne Verwaltungsbereich: hier steht, welche
 * Entscheidungsbäume für die Rechtsgeschäftsart dieses Vorgangs freigegeben sind;
 * nach der Wahl entsteht ein Auftrag. »In Word öffnen« lädt dazu ein leeres
 * Word-Dokument herunter, das die DiNo-Leiste selbst aufmacht und den Auftrag
 * kennt — kein Suchen in Word. Geschrieben wird die Urkunde dort.
 *
 * Bewusst klein: keine Fragen, keine Vorschau, kein Dokumentplan. Das alles
 * passiert in Word, wo der Text auch hinsoll.
 */

interface IProps {
  idLegalTransaction: number;
  idLegalTransactionType?: number | null;
  /**
   * »In DiNo erstellen« (#1245, Teil 48): der fertige Entwurf geht an die Entwurfsliste des
   * Vorgangs. Ohne diesen Weg gibt es den Knopf nicht.
   */
  onDraftCreated?: (_document: IDocument) => void;
}

export const DeedGenerationPanel: React.FC<IProps> = ({
  idLegalTransaction,
  idLegalTransactionType,
  onDraftCreated,
}) => {
  const { showAlert } = useAlert();
  const { deedOffer, isLoadingDeedOffer, refetchDeedOffer } =
    useDeedTemplates(idLegalTransaction);
  const { deedRuns } = useDeedRunsOfLegalTransaction(idLegalTransaction);
  const { startDeedRun, isStartingDeedRun } = useStartDeedRun();
  const { cancelDeedRun } = useCancelDeedRun();
  const { fetchDeedRunWordDocument } = useDeedRunWordDocument();

  const [open, setOpen] = useState(false);
  const [inDino, setInDino] = useState(false);
  const [chosen, setChosen] = useState<number | null>(null);
  const [variant, setVariant] = useState<string | null>(null);

  const templates = useMemo(() => deedOffer?.templates ?? [], [deedOffer]);
  const offered = useMemo(() => offeredTemplates(templates), [templates]);
  const mode = offerMode(templates);

  const chosenTemplate: IDeedTemplate | null =
    offered.find(entry => entry.idDecisionTemplate === chosen) ?? null;

  useEffect(() => {
    setChosen(
      preselectedTemplate(templates, deedOffer?.defaultTemplateId ?? null)
    );
  }, [templates, deedOffer?.defaultTemplateId]);

  useEffect(() => {
    setVariant(initialVariant(chosenTemplate));
  }, [chosenTemplate]);

  /** Das Word-Dokument zum Auftrag herunterladen; gibt den Dateinamen zurück. */
  const openInWord = async (idDecisionDocumentRun: number) => {
    const file = await fetchDeedRunWordDocument(idDecisionDocumentRun);
    triggerBrowserDownload(
      base64ToBlob(file.data, file.mimeType),
      file.fileName
    );
    return file.fileName;
  };

  const openAgain = async (idDecisionDocumentRun: number) => {
    try {
      const fileName = await openInWord(idDecisionDocumentRun);
      showAlert({
        text: `»${fileName}« wird heruntergeladen.`,
        severity: 'success',
      });
    } catch (error) {
      showAlert({
        text: `Das Word-Dokument ließ sich nicht herunterladen: ${apiErrorText(error)}`,
        severity: 'error',
      });
    }
  };

  const start = async () => {
    if (!chosenTemplate) return;
    try {
      const run = await startDeedRun({
        idLegalTransaction,
        idDecisionTemplate: chosenTemplate.idDecisionTemplate,
        ...(variant ? { variant } : {}),
      });
      setOpen(false);
      try {
        const fileName = await openInWord(run.idDecisionDocumentRun);
        showAlert({
          // Heruntergeladene Dateien öffnet Word geschützt; Add-ins laufen erst nach »Bearbeitung
          // aktivieren« (in Word gesehen, 27.10.2026).
          text: `»${fileName}« wird heruntergeladen. Die Datei öffnen und in Word auf »Bearbeitung aktivieren« klicken — dann geht die DiNo-Leiste mit dieser Urkunde auf.`,
          severity: 'success',
        });
      } catch {
        // Der Auftrag steht trotzdem: die Leiste in Word zeigt ihn unter »Urkunde aus DiNo«.
        showAlert({
          text: `„${run.templateName}" steht bereit, aber das Word-Dokument ließ sich nicht herunterladen. In Word die DiNo-Leiste öffnen — die Urkunde steht dort unter »Urkunde aus DiNo«.`,
          severity: 'warning',
        });
      }
    } catch (error) {
      // Der Server sagt, warum: »nicht (mehr) freigegeben«, »Kennung frage1 steht für drei
      // verschiedene Fragen« … Die Liste kann veraltet sein — die Startprüfung ist gründlicher
      // als das Angebot —, deshalb wird sie danach neu geholt (#1245, Teil 38).
      showAlert({
        text: `Die Urkunde konnte nicht gestartet werden: ${apiErrorText(error)}`,
        severity: 'error',
      });
      void refetchDeedOffer();
    }
  };

  const waiting = deedRuns.filter(
    run => run.status === 'OPEN' || run.status === 'CLAIMED'
  );
  const done = deedRuns.filter(run => run.status === 'INSERTED');

  if (!idLegalTransactionType) {
    return null;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          justifyContent='space-between'
          flexWrap='wrap'
        >
          <Box>
            <Typography variant='h6'>Urkunde erstellen</Typography>
            <Typography variant='body2' color='text.secondary'>
              {deedOffer?.legalTransactionType
                ? `Rechtsgeschäftsart: ${deedOffer.legalTransactionType}`
                : 'Rechtsgeschäftsart wird geladen …'}
            </Typography>
          </Box>
          <Stack direction='row' spacing={1}>
            {onDraftCreated && (
              <Button
                variant='outlined'
                startIcon={<EditNoteIcon />}
                disabled={isLoadingDeedOffer || mode === 'none'}
                onClick={() => setInDino(true)}
              >
                In DiNo erstellen
              </Button>
            )}
            <Button
              variant='contained'
              startIcon={<ArticleIcon />}
              disabled={
                isLoadingDeedOffer || mode === 'none' || isStartingDeedRun
              }
              onClick={() => (mode === 'single' ? void start() : setOpen(true))}
            >
              {isStartingDeedRun ? 'Wird vorbereitet …' : 'In Word öffnen'}
            </Button>
          </Stack>
        </Stack>

        {isLoadingDeedOffer && (
          <Box sx={{ mt: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!isLoadingDeedOffer && mode === 'none' && (
          <Alert severity='info' sx={{ mt: 2 }}>
            {deedOffer?.idLegalTransactionType
              ? NO_TEMPLATE_TEXT
              : NO_TRANSACTION_TYPE_TEXT}
          </Alert>
        )}

        {!isLoadingDeedOffer && mode === 'single' && (
          <Typography variant='body2' sx={{ mt: 2 }}>
            {templateLabel(offered[0])} · Version {offered[0].versionNumber}
          </Typography>
        )}

        {!isLoadingDeedOffer && mode === 'choice' && (
          <Typography variant='body2' sx={{ mt: 2 }}>
            {offered.length} Entscheidungsbäume stehen zur Auswahl.
          </Typography>
        )}

        {waiting.length > 0 && (
          <Alert severity='success' sx={{ mt: 2 }} icon={<ArticleIcon />}>
            <Stack spacing={1}>
              {waiting.map(run => (
                <Stack
                  key={run.idDecisionDocumentRun}
                  direction='row'
                  spacing={1}
                  alignItems='center'
                  flexWrap='wrap'
                >
                  <span>
                    {runProvenance(run)} — {runStatusText(run)}
                  </span>
                  <Button
                    size='small'
                    onClick={() => void openAgain(run.idDecisionDocumentRun)}
                  >
                    In Word öffnen
                  </Button>
                  <Button
                    size='small'
                    color='inherit'
                    onClick={() =>
                      void cancelDeedRun(run.idDecisionDocumentRun)
                    }
                  >
                    Verwerfen
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Alert>
        )}

        {done.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant='subtitle2' gutterBottom>
              Bereits erzeugt
            </Typography>
            <Stack spacing={0.5}>
              {done.map(run => (
                <Typography
                  key={run.idDecisionDocumentRun}
                  variant='body2'
                  color='text.secondary'
                >
                  {runProvenance(run)} — {runStatusText(run)}
                </Typography>
              ))}
            </Stack>
          </>
        )}
      </CardContent>

      {onDraftCreated && inDino && (
        <DinoDeedDialog
          open={inDino}
          onClose={() => setInDino(false)}
          idLegalTransaction={idLegalTransaction}
          templates={offered}
          initialTemplate={chosen}
          onDraft={onDraftCreated}
        />
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Urkunde erstellen</DialogTitle>
        <DialogContent>
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Entscheidungsbaum
          </Typography>
          <RadioGroup
            value={chosen === null ? '' : String(chosen)}
            onChange={event => setChosen(Number(event.target.value))}
          >
            {offered.map(entry => (
              <FormControlLabel
                key={entry.idDecisionTemplate}
                value={String(entry.idDecisionTemplate)}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant='body1'>
                      {templateLabel(entry)}
                      {entry.isDefault && (
                        <Chip size='small' label='Vorgabe' sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    {entry.description && (
                      <Typography variant='caption' color='text.secondary'>
                        {entry.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
            ))}
          </RadioGroup>

          {needsVariantChoice(chosenTemplate) && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id='deed-variant'>Variante</InputLabel>
              <Select
                labelId='deed-variant'
                label='Variante'
                value={variant ?? ''}
                onChange={event => setVariant(String(event.target.value))}
              >
                {(chosenTemplate?.variants ?? []).map(entry => (
                  <MenuItem key={entry} value={entry}>
                    {entry}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button
            variant='contained'
            disabled={chosen === null || isStartingDeedRun}
            onClick={() => void start()}
          >
            In Word öffnen
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
